// Sound effects manager using Web Audio API
// Generates simple synth sounds without external audio files

type SoundType =
  | 'click'
  | 'success'
  | 'levelUp'
  | 'milestone'
  | 'complete'
  | 'error'
  | 'coin'
  | 'whoosh'
  | 'pop'
  | 'fanfare'

let audioContext: AudioContext | null = null

const getAudioContext = (): AudioContext => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
  }
  return audioContext
}

// Simple oscillator-based sound generator
const playTone = (
  frequency: number,
  duration: number,
  type: OscillatorType = 'sine',
  volume: number = 0.3,
  delay: number = 0
) => {
  const ctx = getAudioContext()
  const oscillator = ctx.createOscillator()
  const gainNode = ctx.createGain()

  oscillator.connect(gainNode)
  gainNode.connect(ctx.destination)

  oscillator.type = type
  oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + delay)

  // Envelope
  gainNode.gain.setValueAtTime(0, ctx.currentTime + delay)
  gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + delay + 0.01)
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + duration)

  oscillator.start(ctx.currentTime + delay)
  oscillator.stop(ctx.currentTime + delay + duration)
}

// Sound definitions
const sounds: Record<SoundType, () => void> = {
  click: () => {
    playTone(800, 0.05, 'square', 0.1)
  },

  success: () => {
    playTone(523, 0.1, 'sine', 0.2) // C5
    playTone(659, 0.1, 'sine', 0.2, 0.1) // E5
    playTone(784, 0.15, 'sine', 0.2, 0.2) // G5
  },

  levelUp: () => {
    playTone(523, 0.1, 'square', 0.15) // C5
    playTone(659, 0.1, 'square', 0.15, 0.1) // E5
    playTone(784, 0.1, 'square', 0.15, 0.2) // G5
    playTone(1047, 0.2, 'square', 0.2, 0.3) // C6
  },

  milestone: () => {
    playTone(440, 0.1, 'sine', 0.2) // A4
    playTone(554, 0.1, 'sine', 0.2, 0.1) // C#5
    playTone(659, 0.15, 'sine', 0.25, 0.2) // E5
  },

  complete: () => {
    playTone(659, 0.08, 'sine', 0.2) // E5
    playTone(784, 0.12, 'sine', 0.25, 0.08) // G5
  },

  error: () => {
    playTone(200, 0.15, 'sawtooth', 0.15)
    playTone(150, 0.2, 'sawtooth', 0.1, 0.1)
  },

  coin: () => {
    playTone(987, 0.05, 'square', 0.15) // B5
    playTone(1319, 0.1, 'square', 0.15, 0.05) // E6
  },

  whoosh: () => {
    const ctx = getAudioContext()
    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)

    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(400, ctx.currentTime)
    oscillator.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.15)

    gainNode.gain.setValueAtTime(0.1, ctx.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15)

    oscillator.start()
    oscillator.stop(ctx.currentTime + 0.15)
  },

  pop: () => {
    playTone(600, 0.05, 'sine', 0.2)
  },

  fanfare: () => {
    // Triumphant chord progression
    playTone(523, 0.15, 'square', 0.1) // C5
    playTone(659, 0.15, 'square', 0.1) // E5
    playTone(784, 0.15, 'square', 0.1) // G5

    playTone(587, 0.15, 'square', 0.1, 0.2) // D5
    playTone(740, 0.15, 'square', 0.1, 0.2) // F#5
    playTone(880, 0.15, 'square', 0.1, 0.2) // A5

    playTone(659, 0.25, 'square', 0.15, 0.4) // E5
    playTone(784, 0.25, 'square', 0.15, 0.4) // G5
    playTone(1047, 0.25, 'square', 0.15, 0.4) // C6
  },
}

// Check if sound is enabled from settings
let soundEnabled = true

export const setSoundEnabled = (enabled: boolean) => {
  soundEnabled = enabled
}

export const playSound = (type: SoundType) => {
  if (!soundEnabled) return

  try {
    sounds[type]()
  } catch (error) {
    // Audio context might not be available
    console.warn('Sound playback failed:', error)
  }
}

// Hook for components to use sounds
export const useSounds = () => {
  return {
    playClick: () => playSound('click'),
    playSuccess: () => playSound('success'),
    playLevelUp: () => playSound('levelUp'),
    playMilestone: () => playSound('milestone'),
    playComplete: () => playSound('complete'),
    playError: () => playSound('error'),
    playCoin: () => playSound('coin'),
    playWhoosh: () => playSound('whoosh'),
    playPop: () => playSound('pop'),
    playFanfare: () => playSound('fanfare'),
  }
}
